import React, { useEffect } from "react";
import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import api from "../../../config/URL";
import { toast } from "react-toastify";
import { Link, useParams } from "react-router-dom";
import CustomDatePicker from "../../CustomDatePicker";

const validationSchema = Yup.object({
  effectiveDate: Yup.string().required("*Effective Date is required"),
  depositFees: Yup.number()
    .typeError("*Must be a Number")
    .required("*Deposit Fees Code is required"),
  taxTypeId: Yup.string().required("*TaxType is required"),
  status: Yup.string().required("*Status is required"),
});

function CourseFeesAdd({ onSuccess }) {
  const { id } = useParams();
  const [show, setShow] = useState(false);
  const [loadIndicator, setLoadIndicator] = useState(false);
  const [taxData, setTaxData] = useState([]);
  const userName = localStorage.getItem("userName");
  const [isModified, setIsModified] = useState(false);

  const handleClose = () => {
    setShow(false);
    formik.resetForm();
  };

  const handleShow = () => {
    setShow(true);
    setIsModified(false);
    fetchTaxData();
  };

  const fetchTaxData = async () => {
    try {
      const response = await api.get("getAllTaxSetting");
      setTaxData(response.data);
    } catch (error) {
      toast.error("Error fetching tax data:", error);
    }
  };

  const formik = useFormik({
    initialValues: {
      effectiveDate: "",
      depositFees: "",
      taxTypeId: "",
      status: "ACTIVE",
      createdBy: userName,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoadIndicator(true);
      try {
        const response = await api.post(
          `/createCourseDepositFees/${id}`,
          values,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        if (response.status === 201) {
          onSuccess();
          handleClose();
          toast.success(response.data.message);
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error(error);
      } finally {
        setLoadIndicator(false);
      }
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validate: (values) => {
      // Check if any field has a non-empty string value
      const isAnyFieldModified = Object.values(values).some((value) => {
        // Only call trim() if value is a string and not empty
        return typeof value === "string" && value.trim() !== "";
      });
      setIsModified(isAnyFieldModified);
    },
  });

  useEffect(() => {
    fetchTaxData();
  }, [show]);

  return (
    <>
      <div className="py-2 d-flex justify-content-end">
        <Link to="/course">
          <button type="button" className="btn btn-sm btn-border">
            Back
          </button>
        </Link>
          
        <button
          type="button"
          className="btn btn-button btn-sm"
          onClick={handleShow}
        >
          Add <i className="bx bx-plus"></i>
        </button>
      </div>
      <Modal
        show={show}
        size="lg"
        onHide={handleClose}
        centered
        backdrop={isModified ? "static" : true}
        keyboard={isModified ? false : true}
      >
        <Modal.Header closeButton>
          <Modal.Title className="headColor">Add Course Deposit</Modal.Title>
        </Modal.Header>
        <form
          onSubmit={formik.handleSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !formik.isSubmitting) {
              e.preventDefault(); // Prevent default form submission
            }
          }}
        >
          <Modal.Body>
            <div className="container">
              <div className="row py-4">
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">
                    Effective Date<span className="text-danger">*</span>
                  </label>
                  <CustomDatePicker
                    name="effectiveDate"
                    value={formik.values.effectiveDate}
                    onChange={(date) => formik.setFieldValue("effectiveDate", date)}
                    onBlur={formik.handleBlur}
                    error={formik.errors.effectiveDate}
                    touched={formik.touched.effectiveDate}
                    showPopperArrow={true}
                  />
                  {formik.touched.effectiveDate && formik.errors.effectiveDate && (
                    <div className="error text-danger">
                      {formik.errors.effectiveDate}
                    </div>
                  )}
                </div>
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">
                    Deposit Fees<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      formik.touched.depositFees && formik.errors.depositFees
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("depositFees")}
                  />
                  {formik.touched.depositFees && formik.errors.depositFees && (
                    <div className="invalid-feedback">
                      {formik.errors.depositFees}
                    </div>
                  )}
                </div>
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">
                    Tax Type<span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${
                      formik.touched.taxTypeId && formik.errors.taxTypeId
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("taxTypeId")}
                    style={{ width: "100%" }}
                  >
                    <option value=""></option>
                    {taxData &&
                      taxData.map((tax) => (
                        <option key={tax.id} value={tax.id}>
                          {tax.taxType}
                        </option>
                      ))}
                  </select>
                  {formik.touched.taxTypeId && formik.errors.taxTypeId && (
                    <div className="invalid-feedback">
                      {formik.errors.taxTypeId}
                    </div>
                  )}
                </div>
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">
                    Status<span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${
                      formik.touched.status && formik.errors.status
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("status")}
                    style={{ width: "100%" }}
                  >
                    <option value=""></option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                  {formik.touched.status && formik.errors.status && (
                    <div className="invalid-feedback">
                      {formik.errors.status}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              className="btn btn-sm btn-border bg-light text-dark"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="btn btn-button btn-sm"
              disabled={loadIndicator}
            >
              {loadIndicator && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                ></span>
              )}
              Submit
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
}

export default CourseFeesAdd;